// ============================================================
// Onglet « Offres matière » — §1.5.
// ------------------------------------------------------------
// Charles reçoit des offres fournisseur par PDF, et aujourd'hui elles se
// perdent. Rangées ici, elles servent trois fois :
//
//  1. elles JUSTIFIENT le prix d'un devis, retrouvable des mois plus
//     tard — « pourquoi l'inox était à 8,40 en mars » ;
//  2. elles s'empilent par nuance, la plus récente en tête ;
//  3. elles deviennent la SOURCE DE PRIX PAR DÉFAUT (§2.1), devant le
//     référentiel.
//
// ── Pourquoi le formulaire est si court ──
//
// Trois champs obligatoires : la nuance, la date, le prix. Le reste est
// facultatif. C'est délibéré et c'est la même raison qui fait refuser le
// catalogue d'opérations saisi à la main (§7) : **un référentiel qui
// demande douze champs reste vide.** Déposer une offre doit prendre dix
// secondes, sinon la table ne se remplira jamais et la boucle
// d'apprentissage du prix matière n'existera que sur le papier.
// ============================================================

import { useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useCrudList } from './useCrudList.js';
import { formatEuro } from '../../lib/format.js';
import { OFFRE_PERIMEE_JOURS } from '@shared/matiere.js';
import Badge from '../../ui/Badge.jsx';
import { api } from '../../lib/api.js';
import { useToast } from '../../ui/Toast.jsx';

const EMPTY = {
  material_id: null, fournisseur: '', date_offre: '', prix_kg: null,
  quantite_offerte: '', forme: '', reference_offre: '', note: '',
};

/** Une date en clair, sans dépendre du fuseau du navigateur. */
const enClair = (d) => (d ? new Date(d).toLocaleDateString('fr-BE') : '—');

export function OffresPanel({ isAdmin, materials = [] }) {
  const list = useCrudList({ path: '/catalogue/offres-matiere', key: 'offres', label: 'Offre' });
  const [form, setForm] = useState(EMPTY);
  const [filtre, setFiltre] = useState('');
  const toast = useToast();

  const nuances = new Map(materials.map((m) => [m.id, m.designation]));

  const rows = filtre
    ? list.rows.filter(
        (o) =>
          String(nuances.get(o.material_id) ?? '').toLowerCase().includes(filtre.toLowerCase()) ||
          String(o.fournisseur ?? '').toLowerCase().includes(filtre.toLowerCase()),
      )
    : list.rows;

  async function supprimer(id) {
    try {
      await api.delete(`/catalogue/offres-matiere/${id}`);
      toast.success('Offre supprimée.');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    }
  }

  const columns = [
    {
      key: 'material_id',
      label: 'Nuance',
      render: (o) => o.material_designation ?? nuances.get(o.material_id) ?? '—',
    },
    {
      key: 'date_offre',
      label: 'Date',
      render: (o) => {
        // Une offre de plus de six mois reste PROPOSÉE — un vrai prix de
        // mars vaut mieux qu'un défaut de famille — mais elle s'affiche
        // « à reconfirmer ». Ce qui change, c'est ce qu'on en dit.
        const jours = Math.floor((Date.now() - new Date(o.date_offre).getTime()) / 86400000);
        return (
          <span className="row">
            {enClair(o.date_offre)}
            {jours > OFFRE_PERIMEE_JOURS ? <Badge tone="warn">à reconfirmer</Badge> : null}
          </span>
        );
      },
    },
    { key: 'prix_kg', label: 'Prix au kilo', numeric: true, render: (o) => formatEuro(o.prix_kg) },
    { key: 'fournisseur', label: 'Fournisseur', render: (o) => o.fournisseur || '—' },
    { key: 'forme', label: 'Forme', render: (o) => o.forme || '—' },
    { key: 'quantite_offerte', label: 'Quantité', render: (o) => o.quantite_offerte || '—' },
    { key: 'reference_offre', label: 'Réf. offre', render: (o) => o.reference_offre || '—' },
  ];

  if (isAdmin) {
    columns.push({
      key: 'actions',
      label: '',
      render: (o) => (
        <Button size="sm" variant="ghost" onClick={() => supprimer(o.id)}>
          Supprimer
        </Button>
      ),
    });
  }

  if (list.loading) return <SkeletonRows rows={5} />;
  if (list.error) return <ErrorState error={list.error} onRetry={list.reload} />;

  return (
    <Card>
      <CardHead title="Offres matière">
        {isAdmin ? (
          <Button size="sm" variant="primary" onClick={() => { setForm(EMPTY); list.setEditing({}); }}>
            Déposer une offre
          </Button>
        ) : null}
      </CardHead>
      <CardBody className="stack">
        <p className="field__hint">
          Une offre fournisseur est la meilleure source de prix qui existe : un vrai prix, chez un
          vrai fournisseur, à une vraie date. Elle passe devant le référentiel pour toutes les
          pièces de cette nuance. Au bout de quelques mois, le programme connaît les vrais prix de
          Mertens et n’a plus besoin d’aller chercher en ligne.
        </p>

        <TextField
          label="Filtrer par nuance ou fournisseur"
          value={filtre}
          onChange={(e) => setFiltre(e.target.value)}
        />

        {rows.length === 0 ? (
          <EmptyState
            title="Aucune offre enregistrée"
            hint="Déposez la première : la nuance, la date et le prix suffisent."
          />
        ) : (
          <Table columns={columns} rows={rows} rowKey={(o) => o.id} />
        )}
      </CardBody>

      {list.editing ? (
        <Modal
          open
          title="Déposer une offre"
          onClose={() => list.setEditing(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => list.setEditing(null)}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" form="form-offre" disabled={list.pending}>
                {list.pending ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </>
          }
        >
          <form
            id="form-offre"
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await list.save(
                { ...form, material_id: Number(form.material_id) },
                null,
              );
              if (ok) setForm(EMPTY);
            }}
          >
            <Select
              label="Nuance"
              required
              value={form.material_id ? String(form.material_id) : ''}
              error={list.errors.material_id}
              options={[
                { value: '', label: '— choisir —' },
                ...materials.map((m) => ({ value: String(m.id), label: m.designation })),
              ]}
              onChange={(e) => setForm({ ...form, material_id: e.target.value })}
            />
            <div className="grid-2">
              <TextField
                label="Date de l’offre"
                type="date"
                required
                value={form.date_offre}
                error={list.errors.date_offre}
                onChange={(e) => setForm({ ...form, date_offre: e.target.value })}
              />
              <NumberField
                label="Prix au kilo"
                unit="€/kg"
                required
                min={0}
                value={form.prix_kg}
                error={list.errors.prix_kg}
                onChange={(v) => setForm({ ...form, prix_kg: v })}
              />
            </div>
            {/* Tout ce qui suit est FACULTATIF. Le dire à l'écran évite
                qu'on croie devoir le remplir — et c'est ce qui fait la
                différence entre un référentiel vivant et une table vide. */}
            <p className="field__hint">Le reste est facultatif.</p>
            <div className="grid-2">
              <TextField label="Fournisseur" value={form.fournisseur}
                onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} />
              <TextField label="Référence de l’offre" value={form.reference_offre}
                onChange={(e) => setForm({ ...form, reference_offre: e.target.value })} />
              <TextField label="Forme" value={form.forme} hint="Barre ronde Ø40, tôle 10 mm…"
                onChange={(e) => setForm({ ...form, forme: e.target.value })} />
              <TextField label="Quantité offerte" value={form.quantite_offerte}
                onChange={(e) => setForm({ ...form, quantite_offerte: e.target.value })} />
            </div>
            <TextField label="Note" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </form>
        </Modal>
      ) : null}
    </Card>
  );
}

export default OffresPanel;
